import { IProduct } from '../../interfaces'
import { addAlert } from '../../redux/slices/config'
import { formatMaxQuantityMessage } from '../../utils/cart/maxQuantityMessage'
import {
  normalizeQuantity,
  QuantityAdjustmentReason,
  type ICalculateNewQuantity,
} from '../../utils/cart/normalizeQuantity'
import useAppDispatch from '../useAppDispatch'
import useAppSelector from '../useAppSelector'
import useAddItemToCart from './useAddItemToCart'
import useRemoveItemFromCart from './useRemoveItemFromCart'

interface IAdjustProductQuantity
  extends Pick<
    ICalculateNewQuantity,
    'originalValue' | 'modifiedValue' | 'inRangeValue' | 'jumpQty' | 'resultFixed' | 'resultValue' | 'nextValidValue' | 'prevValidValue' | 'ruleInvalidatedBy' | 'isValueOutOfRange' | 'cap' | 'isClampedToCap'
  > {
  product: IProduct
  /** Which limit produced the counter's cap; see `IAddCartProductArgs.capSource`. */
  capSource?: string
}

/**
 * Resolves the ceiling the adjusted quantity has to respect.
 *
 * The counter's own `cap` wins when it forwarded one: it is assembled from inputs
 * the cart cannot see, the `ItemList` "remaining items" ceiling in particular, so
 * second-guessing it here would clamp to a looser limit than the one the customer
 * was shown. The `typeof` check is load-bearing despite the declared type — an
 * unresolved `{{this.cap}}` reaches the runtime as the literal template string
 * (see the strict comparison in `normalizeQuantity` for the same hazard).
 *
 * The fallback covers counters that forward nothing: older library builds, and any
 * consumer that overrode `onInputChange` in config. It deliberately consults only
 * `stock` and **not** `maxQty`. `maxQty` reaches the counter through the
 * `itemPropAsMax` binding, which is the documented way to switch the per-product
 * cap off from config — re-deriving it here would quietly override that decision.
 * `stock` is not config-gated; it is a hard availability limit either way.
 *
 * A non-positive stock is treated as no ceiling rather than as a ceiling of zero:
 * zero means unavailable, and clamping a quantity to it would empty the line.
 * Availability is enforced by the counter's disabled state, not here.
 */
export const resolveQuantityCap = (
  product: Pick<IProduct, 'stock'>,
  capFromCounter: number | undefined,
): number | undefined => {
  if (typeof capFromCounter === 'number' && Number.isFinite(capFromCounter)) {
    return capFromCounter
  }
  const { stock } = product
  return typeof stock === 'number' && Number.isFinite(stock) && stock > 0
    ? stock
    : undefined
}

export const useAdjustProductQuantity = () => {
  const { addItemAction } = useAddItemToCart()
  const { removeItemAction } = useRemoveItemFromCart()
  const dispatch = useAppDispatch()

  const freegoodsAsSubitem = useAppSelector(
    (state) => !!state.defaultSlice.config?.options?.freegoodsAsSubitem,
  )

  const autoAdd = useAppSelector(
    (state) =>
      !!state.defaultSlice.sessionData?.configuration?.promotions?.autoAdd,
  )

  const currency = useAppSelector(
    (state) => state.defaultSlice.config?.options?.currencyPrefix || '',
  )

  const updateQuantityMessage = useAppSelector(
    (state) => state.defaultSlice.config?.texts?.cart.updateQuantityMessage,
  )

  const maxQuantityMessage = useAppSelector(
    (state) => state.defaultSlice.config?.texts?.cart.maxQuantityMessage,
  )

  const quantityAdjustment = useAppSelector(
    (state) => state.defaultSlice.sessionData?.configuration?.cart?.quantityAdjustment,
  )

  // Read from config rather than taken as an action argument: an SCS override of
  // counter.onInputChange replaces the whole cinnamonActions array, which would drop
  // the argument while the Counter prop stayed enabled and leave the two halves of
  // this flag disagreeing. Compared strictly so a stringy config value cannot enable
  // a destructive path.
  const allowTypedZero = useAppSelector(
    (state) =>
      state.defaultSlice.config?.globals?.counter?.allowTypedZero === true,
  )

  const adjustProductQuantity = (
    args: IAdjustProductQuantity,
  ): void | number => {
    const {
      product,
      product: { quantity: value, promotionalQty, minQtyAllowed, minQty },
      resultFixed,
      resultValue,
      nextValidValue,
      prevValidValue,
      ruleInvalidatedBy,
      cap,
      isClampedToCap,
      capSource,
      ...restArgs
    } = args

    const outcome = normalizeQuantity({
      ...restArgs,
      resultValue,
      resultFixed,
      promotionalQty,
      minQtyAllowed,
      freegoodsAsSubitem,
      autoAdd,
      minQty,
      productQuantity: value,
      quantityAdjustment,
      nextValidValue,
      prevValidValue,
      ruleInvalidatedBy,
      allowTypedZero,
      cap: resolveQuantityCap(product, cap),
      isClampedToCap,
    })
    const { value: newValue, reason } = outcome

    // The alert reports what happened to the quantity. A capped quantity gets the
    // ceiling named — but only where a storefront has worded that message; without
    // one there is nothing new to say and the generic message takes over below.
    const maxCapMessage =
      outcome.reason === QuantityAdjustmentReason.MAX_CAP
        ? formatMaxQuantityMessage(maxQuantityMessage, outcome.cap)
        : undefined

    // Whether the committed quantity differs from what was asked for. The reasons
    // are checked as well as the numbers because the counter now normalizes on the
    // way in: a jump-floored entry arrives with `inRangeValue` already floored, so
    // the numeric comparison alone no longer sees the adjustment it used to catch,
    // and the customer would be given a quantity they did not type with nothing
    // said about it.
    //
    // A real clamp is on this list for the same reason, and it is the case that
    // needs it most: the ceiling wording is opt-in and ships blank, so without it
    // here a storefront that has not worded one loses the alert it used to get
    // instead of falling back to it. An unsatisfiable cap is deliberately absent —
    // there the line was left alone, and "we adjusted your quantity" would be untrue.
    const quantityWasAdjusted =
      reason === QuantityAdjustmentReason.JUMP_FLOOR ||
      reason === QuantityAdjustmentReason.MIN_QTY ||
      reason === QuantityAdjustmentReason.ROUNDING_RULE ||
      (outcome.reason === QuantityAdjustmentReason.MAX_CAP && outcome.clamped) ||
      (!resultFixed && newValue !== args.inRangeValue)

    const alertMessage =
      maxCapMessage ?? (quantityWasAdjusted ? updateQuantityMessage : undefined)

    if (alertMessage) {
      dispatch(
        addAlert({
          message: alertMessage,
          showIcon: true,
          severity: 'info',
        }),
      )
    }

    if (product.quantity === newValue) {
      return newValue
    }
    if (product.quantity > newValue) {
      removeItemAction({
        product,
        value,
        newValue,
        currency,
      })
    } else {
      addItemAction({
        product,
        value,
        newValue,
        resultFixed,
        // Forwarded so the add path can tell a stock pin from a ceiling clamp.
        // Without them it reports every fixed result as "only N left in stock",
        // naming the product's stock even when the limit was the customer's
        // purchase ceiling.
        isClampedToCap,
        capSource,
      })
    }
  }

  return {
    adjustProductQuantity,
  }
}

export default useAdjustProductQuantity
